import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";

import { cancelFiled } from "../../services/backend/CorrespondenceService";
import { summarizeCancelNotice } from "./cancelNoticeSummary";

// Past this many addressees the list collapses behind "ver todos".
const PEOPLE_SHOWN = 6;

// Keep in sync with CANCEL_REASON_MIN/MAX in gfunctions/handleCorrespondence.
const REASON_MIN = 10;
const REASON_MAX = 1000;

const FALLBACK_ERRORS = {
  0: "No hay conexión con el servidor. Revisa tu conexión e inténtalo de nuevo; el radicado no se anuló.",
  400: "El motivo de anulación no es válido.",
  404: "El radicado no existe.",
  409: "Este radicado ya no se puede anular.",
};

// The function answers a missing or expired token with a bare-text 403, and a
// missing permission with a JSON one carrying its own message.
const SESSION_EXPIRED = "Tu sesión expiró. Recarga la página, vuelve a iniciar sesión e inténtalo de nuevo.";

// Asks for the motive and annuls the radicado. The server decides whether it can
// be annulled (permission, status, PQRSD already answered) and says why not.
// `notice` is the function's cancelNotice preview: who annulling will email.
export default function CancelFiledDialog({ open, filedId, finalized, notice, onClose, onCancelled }) {
  const user = useSelector((state) => state.user.value);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setError(null);
      setShowAll(false);
    }
  }, [open]);

  const summary = summarizeCancelNotice(notice, { finalized });
  const people = showAll ? summary.people : summary.people.slice(0, PEOPLE_SHOWN);

  const length = reason.trim().length;
  const valid = length >= REASON_MIN && length <= REASON_MAX;

  const handleClose = () => {
    if (!saving) onClose();
  };

  const handleConfirm = async () => {
    if (!valid) return;
    setSaving(true);
    setError(null);
    const result = await cancelFiled(user.token, { filedId, reason: reason.trim() });
    setSaving(false);
    // Someone else annulled it in the meantime: the outcome the user wanted
    // already holds, and the annulled page shows who did it and why.
    if (result.ok || result.code === "ALREADY_CANCELLED") {
      onCancelled();
      return;
    }
    if (result.status === 403 && !result.error) {
      setError(SESSION_EXPIRED);
      return;
    }
    setError(result.error || FALLBACK_ERRORS[result.status] || "No se pudo anular el radicado. Inténtalo de nuevo.");
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{`Anular radicado No. ${filedId || ""}`}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {finalized
            ? "El radicado ya fue finalizado: los correos enviados, la tarea y la copia en el expediente se conservan, pero quedará marcado como ANULADO en todas partes, incluida la consulta por código QR."
            : "El radicado quedará marcado como ANULADO y ya no se podrá editar ni finalizar."}
          {" "}Esta acción no se puede deshacer.
        </DialogContentText>
        {summary.text && (
          <Alert severity={summary.severity} sx={{ mb: 2, "& .MuiAlert-message": { width: "100%", minWidth: 0 } }}>
            <Typography variant="body2">{summary.text}</Typography>
            {summary.people.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
                {people.map((p) => (
                  <Chip
                    key={p.key}
                    size="small"
                    variant="outlined"
                    label={`${p.label} · ${p.audience}`}
                    sx={{ maxWidth: "100%" }}
                  />
                ))}
                {summary.people.length > PEOPLE_SHOWN && (
                  <Chip
                    size="small"
                    label={showAll ? "Ver menos" : `Ver todos (${summary.people.length})`}
                    onClick={() => setShowAll((v) => !v)}
                  />
                )}
              </Box>
            )}
            {summary.reasonNote && (
              <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
                {summary.reasonNote}
              </Typography>
            )}
          </Alert>
        )}
        <TextField
          autoFocus
          fullWidth
          multiline
          minRows={3}
          label="Motivo de anulación"
          value={reason}
          onChange={(evt) => setReason(evt.target.value.slice(0, REASON_MAX))}
          disabled={saving}
          required
          error={length > 0 && length < REASON_MIN}
          helperText={
            length < REASON_MIN
              ? `Mínimo ${REASON_MIN} caracteres (${length}/${REASON_MAX})`
              : `${length}/${REASON_MAX}`
          }
        />
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="error" variant="contained" onClick={handleConfirm} disabled={!valid || saving}>
          {saving ? <CircularProgress size={22} color="inherit" /> : "Anular radicado"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
